// Catalog.ID Client — Claim Encryption Operations
// ============================================================================
// Handles the client-side cryptographic operations for claim creation and sharing.
//
// Key wrapping protocol (hybrid KEM — X25519 + ML-KEM-1024):
// - Generate a random 32-byte AES symmetric key for the claim value
// - Encrypt the claim value with AES-256-GCM using the symmetric key
// - To encrypt the symmetric key for storage:
//   Hybrid KEM encapsulate with the owner's combined public key.
//   Output: version(1) || epk(32) || ctPq(1568) || salt(32) || nonce(12) || aesCt(48) = 1693 bytes
// - To decrypt:
//   Hybrid KEM decapsulate with the owner's combined secret key → original symmetric key

import * as aes from './aes';
import { kemEncapsulate, kemDecapsulate, HYBRID_KEM_HEADER_BYTES, HKDF_INFO_CID_KEYBAG } from './kem';

const EMPTY_AAD = new Uint8Array(0);

// ============================================================================
// Claim Creation
// ============================================================================

export interface EncryptedClaim {
    encryptedValue: string;  // base64 ciphertext
    nonce: string;           // base64 nonce
    encryptedKey: string;    // base64: hybrid KEM blob (1693 bytes for a 32-byte symmetric key)
    encryptedLabel?: string; // base64 ciphertext (if label provided)
    labelNonce?: string;     // base64 nonce (if label provided)
}

/**
 * Encrypt a claim value (and optionally a label) for storage.
 * 1. Generate a random AES-256 symmetric key
 * 2. Encrypt the claim value with the symmetric key
 * 3. Optionally encrypt the label with the same symmetric key (separate nonce)
 * 4. Wrap the symmetric key with the owner's hybrid public key (X25519 + ML-KEM-1024)
 */
export async function encryptClaim(value: string, ownerPublicKey: Uint8Array, label?: string): Promise<EncryptedClaim> {
    const symKey = aes.generateSymmetricKey();
    const nonce = aes.generateNonce();
    const plaintext = aes.encodeUtf8(value);
    const ciphertext = await aes.encrypt(symKey, nonce, plaintext);
    const wrappedKey = await wrapSymmetricKey(symKey, ownerPublicKey);

    const result: EncryptedClaim = {
        encryptedValue: aes.toBase64(ciphertext),
        nonce: aes.toBase64(nonce),
        encryptedKey: aes.toBase64(wrappedKey),
    };

    if (label) {
        const labelEncrypted = await encryptLabel(label, symKey);
        result.encryptedLabel = labelEncrypted.encryptedLabel;
        result.labelNonce = labelEncrypted.labelNonce;
    }

    return result;
}

/**
 * Decrypt a claim value.
 * 1. Unwrap the symmetric key using the owner's private key
 * 2. Decrypt the claim value with the symmetric key
 */
export async function decryptClaim(
    encryptedValue: string,
    nonce: string,
    encryptedKey: string,
    ownerSecretKey: Uint8Array,
): Promise<string> {
    const symKey = await unwrapSymmetricKey(aes.fromBase64(encryptedKey), ownerSecretKey);
    const ct = aes.fromBase64(encryptedValue);
    const n = aes.fromBase64(nonce);
    const plaintext = await aes.decrypt(symKey, n, ct);
    return aes.decodeUtf8(plaintext);
}

// ============================================================================
// Label Encryption (same symmetric key as claim value)
// ============================================================================

/**
 * Encrypt a label using the same symmetric key as the claim value.
 * Uses a fresh nonce (distinct from the value nonce).
 */
export async function encryptLabel(
    label: string,
    symKey: Uint8Array,
): Promise<{ encryptedLabel: string; labelNonce: string }> {
    const nonce = aes.generateNonce();
    const plaintext = aes.encodeUtf8(label);
    const ciphertext = await aes.encrypt(symKey, nonce, plaintext);
    return {
        encryptedLabel: aes.toBase64(ciphertext),
        labelNonce: aes.toBase64(nonce),
    };
}

/**
 * Decrypt a label using the claim's symmetric key.
 */
export async function decryptLabel(
    encryptedLabel: string,
    labelNonce: string,
    symKey: Uint8Array,
): Promise<string> {
    const ct = aes.fromBase64(encryptedLabel);
    const n = aes.fromBase64(labelNonce);
    const plaintext = await aes.decrypt(symKey, n, ct);
    return aes.decodeUtf8(plaintext);
}

// ============================================================================
// Claim Update (re-encrypt value with existing symmetric key)
// ============================================================================

/**
 * Re-encrypt a claim value using the existing symmetric key.
 * Used for claim updates — preserves the same symmetric key so sharing
 * relationships are unaffected.
 */
export async function reEncryptClaimValue(
    newValue: string,
    existingEncryptedKey: string,
    ownerSecretKey: Uint8Array,
): Promise<{ encryptedValue: string; nonce: string }> {
    const symKey = await unwrapSymmetricKey(aes.fromBase64(existingEncryptedKey), ownerSecretKey);
    const nonce = aes.generateNonce();
    const plaintext = aes.encodeUtf8(newValue);
    const ciphertext = await aes.encrypt(symKey, nonce, plaintext);
    return {
        encryptedValue: aes.toBase64(ciphertext),
        nonce: aes.toBase64(nonce),
    };
}

// ============================================================================
// Claim Sharing
// ============================================================================

/**
 * Re-encrypt a claim's symmetric key for sharing with a recipient.
 * 1. Unwrap the symmetric key using the sharer's private key
 * 2. Wrap it with the recipient's public key
 */
export async function reEncryptClaimKey(
    encryptedKey: string,
    sharerSecretKey: Uint8Array,
    recipientPublicKey: Uint8Array,
): Promise<string> {
    const symKey = await unwrapSymmetricKey(aes.fromBase64(encryptedKey), sharerSecretKey);
    const wrappedKey = await wrapSymmetricKey(symKey, recipientPublicKey);
    return aes.toBase64(wrappedKey);
}

/**
 * Re-encrypt a claim's symmetric key for sharing, with key chain support.
 * Handles multi-layer claim keys produced by key rotation.
 * If keyIndex is 0, delegates to the standard single-layer reEncryptClaimKey.
 */
export async function reEncryptClaimKeyWithChain(
    encryptedKey: string,
    keyChain: { secretKey: Uint8Array; keyIndex: number }[],
    keyIndex: number,
    recipientPublicKey: Uint8Array,
): Promise<string> {
    const symKey = await unwrapSymmetricKeyChain(encryptedKey, keyChain, keyIndex);
    const wrappedKey = await wrapSymmetricKey(symKey, recipientPublicKey);
    return aes.toBase64(wrappedKey);
}

// ============================================================================
// Key Wrapping Internals
// ============================================================================

/**
 * Wrap a symmetric key with a hybrid public key.
 * Output: version(1) || epk(32) || ctPq(1568) || salt(32) || nonce(12) || aesCt(48) = 1693 bytes.
 *
 * Exported because the same "fresh AES key, wrapped to the account's
 * attr_encrypt public key" recipe is reused outside claims — see
 * crypto/device-label.ts, which self-encrypts device names so the server
 * never learns a hostname.
 */
export async function wrapSymmetricKey(symKey: Uint8Array, publicKey: Uint8Array): Promise<Uint8Array> {
    return kemEncapsulate(publicKey, symKey, HKDF_INFO_CID_KEYBAG, EMPTY_AAD);
}

/**
 * Unwrap a symmetric key using a hybrid secret key.
 * Input: version(1) || epk(32) || ctPq(1568) || salt(32) || nonce(12) || aesCt(48) = 1693 bytes.
 */
export async function unwrapSymmetricKey(blob: Uint8Array, secretKey: Uint8Array): Promise<Uint8Array> {
    return kemDecapsulate(secretKey, blob, HKDF_INFO_CID_KEYBAG, EMPTY_AAD);
}

// ============================================================================
// Chain-Walking Decryption (key rotation support)
// ============================================================================

/**
 * Unwrap a symmetric key that may have multiple rotation envelopes.
 * Each rotation adds an outer hybrid KEM + AES-256-GCM envelope.
 * The keyIndex indicates how many layers to peel.
 *
 * @param encryptedKey - base64 encoded multi-layer blob
 * @param keyChain - array of { secretKey, keyIndex } sorted by keyIndex ascending
 * @param keyIndex - the number of rotation layers on this claim key
 */
export async function unwrapSymmetricKeyChain(
    encryptedKey: string,
    keyChain: { secretKey: Uint8Array; keyIndex: number }[],
    keyIndex: number,
): Promise<Uint8Array> {
    let current = aes.fromBase64(encryptedKey);

    // Peel envelopes from outermost (keyIndex) down to 0
    for (let i = keyIndex; i >= 0; i--) {
        const key = keyChain.find(k => k.keyIndex === i);
        if (!key) throw new Error(`Missing key for index ${i} in key chain`);
        current = await kemDecapsulate(key.secretKey, current, HKDF_INFO_CID_KEYBAG, EMPTY_AAD);
    }

    return current; // 32-byte claim symmetric key
}

// ============================================================================
// Arbitrary-Length Key Wrapping
// ============================================================================
// Wraps an arbitrary-length private key (e.g., org hybrid secret key)
// to a recipient's public key. Uses the same AES-GCM + hybrid-KEM-wrapped-
// symmetric-key pattern as claim encryption. The result is serialized as JSON
// into a single string suitable for the server's wrappedKey field.

interface WrappedKeyBlob {
    /** AES-GCM encrypted private key bytes, base64 */
    ev: string;
    /** AES-GCM nonce, base64 */
    n: string;
    /** KEM-wrapped AES symmetric key, base64 (kemCiphertext || nonce || aesCiphertext) */
    ek: string;
}

/**
 * Wrap an arbitrary-length private key to a recipient's hybrid public key.
 * Returns a JSON string encoding the encrypted blob.
 */
export async function wrapPrivateKey(privateKeyBase64: string, recipientPublicKey: Uint8Array): Promise<string> {
    const encrypted = await encryptClaim(privateKeyBase64, recipientPublicKey);
    const blob: WrappedKeyBlob = {
        ev: encrypted.encryptedValue,
        n: encrypted.nonce,
        ek: encrypted.encryptedKey,
    };
    return JSON.stringify(blob);
}

/**
 * Unwrap a private key that was wrapped with wrapPrivateKey().
 * Returns the original base64-encoded private key string.
 */
export async function unwrapPrivateKey(wrappedJson: string, recipientSecretKey: Uint8Array): Promise<string> {
    const blob: WrappedKeyBlob = JSON.parse(wrappedJson);
    return decryptClaim(blob.ev, blob.n, blob.ek, recipientSecretKey);
}
